import { Suspense, useEffect } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import type { ArticleDetailDto } from '@zoxo/shared';

import { ErrorBoundary } from '../components/error-boundary';
import { PageContainer } from '../components/layout/page-container';
import { ArticleDetail } from '../features/articles/article-detail';
import { articleQuery } from '../features/articles/use-article';
import { commentsQuery } from '../features/comments/use-comments';
import { hasSession, useAuth } from '../lib/auth';
import { orNotFound } from '../lib/or-not-found';
import { breadcrumbLd, pageHead, PUBLISHER_LD, type PageMeta } from '../lib/seo';
import { canonical } from '../lib/site';
import { articleSummary } from '../features/articles/format';

function articleMeta(article: ArticleDetailDto): PageMeta {
  const summary = articleSummary(article);
  const url = canonical(`/articles/${article.id}`);
  return {
    title: article.title,
    description: summary,
    url,
    type: 'article',
    image: article.thumbnailUrl ?? undefined,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: summary,
        url,
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        ...(article.thumbnailUrl ? { image: article.thumbnailUrl } : {}),
        // Anonymity: nickname only, never real name / identifier.
        author: { '@type': 'Person', name: article.author.nickname },
        publisher: PUBLISHER_LD,
        datePublished: article.createdAt,
        dateModified: article.updatedAt,
      },
      breadcrumbLd([
        { name: '홈', path: '/' },
        { name: '아티클', path: '/articles' },
        { name: article.title, path: `/articles/${article.id}` },
      ]),
    ],
  };
}

/** 로그인 뒤 한 번만 다시 받는다 — 여전히 404 면 그대로 둔다(무한 재시도 방지). */
const retriedWithSession = new Set<string>();

/**
 * PD 수첩 article detail (index of `/articles/$id`, V4 §C). Suspense read wrapped in an
 * Error Boundary so a missing / unapproved-and-not-owned article (404) shows a
 * fallback instead of a blank screen. resetKey = id clears a
 * stale error when navigating between articles. Sits under the `pd.$id` Outlet
 * layout so the sibling `/edit` route renders as its own full page.
 */
export const Route = createFileRoute('/articles/$id/')({
  loader: async ({ params: { id }, context: { queryClient } }) => {
    // 서버는 비로그인 키로 채운다. 검수 전 글은 비로그인에게 404 — 크롤러에게도 404 다.
    const authed = await hasSession();
    const article = await orNotFound(queryClient.ensureQueryData(articleQuery(id, authed)));
    // 댓글은 실패해도 기사를 막지 않는다.
    await queryClient.ensureQueryData(commentsQuery({ kind: 'article', id })).catch(() => undefined);
    return { meta: articleMeta(article) };
  },
  head: ({ loaderData }) => (loaderData ? pageHead(loaderData.meta) : {}),
  notFoundComponent: ArticleNotFound,
  component: PdDetailPage,
});

/**
 * 없는 기사 — 또는 서버가 비로그인이라 못 본 검수 전 기사. 작성자 · 관리자가 주소를 새로 열면
 * 서버는 404 를 그린다. 세션이 확인되면 loader 를 한 번 다시 돌려 토큰으로 받는다.
 */
function ArticleNotFound() {
  const { id } = Route.useParams();
  const { isAuthenticated } = useAuth();
  const router = useRouter();
  useEffect(() => {
    if (!isAuthenticated || retriedWithSession.has(id)) return;
    retriedWithSession.add(id);
    void router.invalidate();
  }, [isAuthenticated, id, router]);
  return (
    <PageContainer>
      <p className="text-sm text-text-muted">기사를 찾을 수 없어요. 비공개 상태이거나 삭제됐을 수 있어요.</p>
    </PageContainer>
  );
}

function PdDetailPage() {
  const { id } = Route.useParams();
  return (
    <PageContainer>
      <ErrorBoundary
        resetKey={id}
        fallback={() => (
          <p className="text-sm text-text-muted">
            기사를 찾을 수 없어요. 비공개 상태이거나 삭제됐을 수 있어요.
          </p>
        )}
      >
        <Suspense
          fallback={<p className="text-sm text-text-muted">불러오는 중…</p>}
        >
          <ArticleDetail id={id} />
        </Suspense>
      </ErrorBoundary>
    </PageContainer>
  );
}
